import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {listClients, getClientFields, parseQuery, queryData} from '../utils/mcpClient';
import {PROMPT_TEMPLATES, selectPromptsForClient} from '../utils/promptTemplates';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {Link} from 'react-router-dom';

class PlaygroundSectionComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clients: [],
            selectedClientId: '',
            clientDataSources: [],
            loading: false,
            loadingClients: true,
            loadingFields: false,
            results: null,
            columns: [],
            error: null,
            errorType: null,
            activePromptId: null,
        };
    }

    componentDidMount() {
        const {autoClientId} = this.props;

        listClients()
            .then(data => {
                const clients = data.clients || [];
                this.setState({clients, loadingClients: false}, () => {
                    if (autoClientId) {
                        this.handleClientChange(String(autoClientId));
                    }
                });
            })
            .catch(() => {
                this.setState({loadingClients: false});
            });
    }

    handleClientChange(clientId) {
        this.setState({
            selectedClientId: clientId,
            clientDataSources: [],
            loadingFields: !!clientId,
            results: null,
            columns: [],
            error: null,
            errorType: null,
            activePromptId: null,
        });

        if (!clientId) return;

        getClientFields(clientId)
            .then(data => {
                const sources = data.client_data_sources || [];
                this.setState({clientDataSources: sources, loadingFields: false});
            })
            .catch(() => {
                this.setState({clientDataSources: [], loadingFields: false});
            });
    }

    handlePromptClick(prompt) {
        const {selectedClientId} = this.state;
        const {t} = this.props;

        if (!selectedClientId) return;

        this.setState({
            loading: true,
            error: null,
            errorType: null,
            results: null,
            columns: [],
            activePromptId: prompt.id,
        });

        parseQuery(selectedClientId, prompt.query)
            .then(parseResult => {
                if (parseResult.is_valid && parseResult.query) {
                    return queryData(selectedClientId, parseResult.query);
                } else {
                    const msg = parseResult.missing_params
                        ? `Missing: ${parseResult.missing_params.join(', ')}`
                        : (parseResult.invalid_fields
                            ? `Invalid fields: ${parseResult.invalid_fields.join(', ')}`
                            : t('dashbo_mcp.playground.error'));
                    throw new Error(msg);
                }
            })
            .then(queryResult => {
                const rows = queryResult.rows || [];
                const cols = rows.length > 0 ? Object.keys(rows[0]) : [];
                this.setState({results: rows, columns: cols, loading: false});
            })
            .catch(err => {
                this.setState({error: err.message, errorType: err.errorType || '', loading: false});
            });
    }

    render() {
        const {t, autoClientId} = this.props;
        const {
            clients, selectedClientId, clientDataSources,
            loading, loadingClients, loadingFields,
            results, columns, error, errorType, activePromptId,
        } = this.state;

        const filteredPrompts = selectPromptsForClient(PROMPT_TEMPLATES, clientDataSources, 5);
        const showClientSelector = !autoClientId;

        return (
            <div className="dashbo-mcp-section">
                <h2 className="section-title">{t('dashbo_mcp.playground.title')}</h2>
                <p className="section-description">{t('dashbo_mcp.playground.description')}</p>

                {showClientSelector && (
                    <div className="playground-controls">
                        <select
                            className="form-control client-selector"
                            value={selectedClientId}
                            onChange={e => this.handleClientChange(e.target.value)}
                            disabled={loadingClients}
                        >
                            <option value="">{t('dashbo_mcp.playground.select_client')}</option>
                            {clients.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {loadingFields && (
                    <div className="playground-loading-fields">
                        <Spinner/>
                    </div>
                )}

                {showClientSelector && !selectedClientId && !loadingClients && (
                    <div className="playground-empty-state">
                        <FaIcon iconName="hand-pointer"/>
                        <span>{t('dashbo_mcp.playground.empty_state_hint')}</span>
                    </div>
                )}

                {selectedClientId && !loadingFields && (
                    <div className="suggestion-chips">
                        {filteredPrompts.map(prompt => (
                            <span
                                key={prompt.id}
                                className={`chip${activePromptId === prompt.id ? ' -active' : ''}`}
                                onClick={() => !loading && this.handlePromptClick(prompt)}
                            >
                                {t(prompt.labelKey)}
                            </span>
                        ))}
                    </div>
                )}

                {loading && <Spinner/>}

                {error && errorType && errorType.includes('ACCOUNT_CONNECTION_ERROR') ? (
                    <div className="playground-connection-error">
                        <div className="connection-error-header">
                            <FaIcon iconName="link-slash"/>
                            <strong>{t('dashbo_mcp.playground.connection_error_title')}</strong>
                        </div>
                        <p>{t('dashbo_mcp.playground.connection_error_desc')}</p>
                        <Link
                            to={`/client/${selectedClientId}/conections`}
                            className="btn -alt"
                        >
                            {t('dashbo_mcp.playground.connection_error_cta')}
                        </Link>
                    </div>
                ) : error ? (
                    <div className="playground-error">{error}</div>
                ) : null}

                {results && results.length === 0 && !loading && (
                    <p>{t('dashbo_mcp.playground.no_results')}</p>
                )}

                {results && results.length > 0 && (
                    <div className="results-container">
                        <table className="table table-striped">
                            <thead>
                            <tr>
                                {columns.map(col => <th key={col}>{col}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {results.map((row, i) => (
                                <tr key={i}>
                                    {columns.map(col => (
                                        <td key={col}>{row[col] != null ? String(row[col]) : ''}</td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="playground-demo-cta">
                    <Link to="/dashbo-connect/demo-report" className="demo-cta-link">
                        <span className="demo-cta-icon"><FaIcon iconName="chart-line"/></span>
                        <div className="demo-cta-text">
                            <strong>{t('dashbo_mcp.playground.demo_cta_title')}</strong>
                            <span>{t('dashbo_mcp.playground.demo_cta_desc')}</span>
                        </div>
                        <span className="demo-cta-arrow"><FaIcon iconName="arrow-right"/></span>
                    </Link>
                </div>
            </div>
        );
    }
}

export const PlaygroundSection = withTranslation()(PlaygroundSectionComponent);
